import { Badge, Divider, Image, Space, Table, Tag, Tooltip, Typography, message } from "antd"
import React, { useEffect, useState } from "react"
import "./Arsoma.css"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import moment from "moment"
import { NavLink } from "react-router-dom"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { DndContext } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { MenuOutlined } from "@ant-design/icons"
import { ASAPOrder, OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import "../../../styles/OrderTable.css"
import { formatNumberWithSpaces } from "../../../components/Orders/Helpers/FormatNumbersWithSpaces"
import MakeCurrentlyPrinting from "../../../components/Machines/ActivePrinting/MakeCurrentlyPrinting"
import OrderTicketButton from "../../../components/Tickets/OrderTicketDrawer"
import axiosInstance from "../../../axios/axiosConfig"

const { Text } = Typography

const Row = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  })
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      },
    ),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  }
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === "sort") {
          return React.cloneElement(child, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: "none",
                  cursor: "move",
                }}
                {...listeners}
              />
            ),
          })
        }
        return child
      })}
    </tr>
  )
}

export default function ActiveOrders() {
  const [arsomaPrintData, setArsomaPrintData] = useState([])
  //array that manages order and gets updates
  const [arsomaSequence, setArsomaSequence] = useState(arsomaPrintData.map((item) => item.id))
  //loading for while data is sorting
  const [isSorting, setIsSorting] = useState(true)

  const activeOrder = arsomaPrintData.find((r) => r.currentlyInUseForArsoma)

  let clientFIlters =
    arsomaPrintData &&
    arsomaPrintData.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    arsomaPrintData &&
    arsomaPrintData.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  let materialFilter =
    arsomaPrintData &&
    arsomaPrintData.reduce((accumulator, item) => {
      const materialName = item.selectedProduct.material
      const existingItem = accumulator.find((filterItem) => filterItem.text === materialName)

      if (existingItem) {
        existingItem.value = materialName
      } else {
        accumulator.push({
          text: materialName,
          value: materialName,
        })
      }

      return accumulator
    }, [])

  const arsomaPrintingCols = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      width: "10%",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
    },

    {
      title: "Priority",
      width: "3%",
      dataIndex: "id",
      align: "center",
      render: (id) => arsomaSequence.indexOf(id) + 1,
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => (
        <>
          <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>
        </>
      ),
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      key: "created",
      render: (value, record) => {
        const dueDate = moment.unix(value.seconds)
        const isOverdue = dueDate.isBefore(moment(), "day")
        if (isOverdue) {
          return <Tag color="red"> {dueDate.format("DD.MM.YYYY")}</Tag>
        } else {
          return <Tag color="blue">{dueDate.format("DD.MM.YYYY")}</Tag>
        }
      },
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      align: "center",
      width: "7%",
      sorter: (a, b) => a.labelCount - b.labelCount,
      render: (value) => <Text>{formatNumberWithSpaces(value)}</Text>,
    },
    {
      title: "Die-cut",
      dataIndex: ["selectedProduct", "knifeCode"],
      align: "center",
    },

    {
      title: "W",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      width: "3%",
    },
    {
      title: "H",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "3%",
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "center",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value, record) => {
        if (record.readRollWidth) {
          return <Text>{record.rollWidth}</Text>
        } else {
          return <Text>{value}</Text>
        }
      },
    },
    {
      title: "Proj. M.",
      dataIndex: ["projectedMeters"],
      align: "center",
      width: "4%",
      render: (value, record) => <Text>{value}m</Text>,
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "code"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => (
        <>
          <Image src={value} />
        </>
      ),
    },
    {
      title: "Details",
      align: "center",
      render: (value, record) => (
        <Space direction="vertical">
          <NavLink state={value} to={"/arsoma/orders/" + value.id}>
            {value ? "Fill Order" : ""}
          </NavLink>
          <OrderTicketButton order={value} />

          <MakeCurrentlyPrinting
            machine="Arsoma"
            orderId={record.id}
            currentlyInUse={record.currentlyInUseForArsoma}
          />
        </Space>
      ),
    },
  ]

  //gets array of the sequence
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "lvYzqVUCJhcyuwSlJESD")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setArsomaSequence(sequenceData.arsomaSequence)
        console.log("sequence")
      }
    })
    return () => unsubscribe()
  }, [])

  // //fetches the documents, then uses the sequence array to sort in correct order
  useEffect(() => {
    const ref = collection(db, "orders")
    let q = query(
      ref,
      where("orderStatus", "==", 3),
      where("selectedProduct.machine", "==", "Arsoma"),
    )

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        results.push({ ...data, key: doc.id, id: doc.id })
      })

      // Fetch knife data and calculate projected meters in batch
      const ordersWithKnifeData = await Promise.all(
        results.map(async (order) => {
          if (
            order.selectedProduct.knifeCode === undefined ||
            order.selectedProduct.knifeCode === "none"
          ) {
            return { ...order, projectedMeters: 0 } // No knife code, set projected meters to 0
          }
          try {
            const response = await axiosInstance.get(
              `/api/searchByCode?code=${encodeURIComponent(order.selectedProduct.knifeCode)}`,
            )
            const knife = response.data
            const correctColumns = order.selectedProduct.numberOfColumns
            const countInThousands = order.labelCount / 1000

            const a1 = (knife.height + knife.gapRaport) * 0.001
            const a2 = (1000 / correctColumns).toFixed(2)
            const a3 = Math.ceil(a1 * a2)
            const a4 = a3 * countInThousands
            return { ...order, projectedMeters: a4 } // Add calculated projected meters to order
          } catch (error) {
            console.error("Failed to fetch knife data:", error)
            return { ...order, projectedMeters: 0 } // In case of error, set projected meters to 0
          }
        }),
      )

      // sort the results based on the arsomaSequence state
      const sortedResults = ordersWithKnifeData.sort((a, b) => {
        return arsomaSequence.indexOf(a.id) - arsomaSequence.indexOf(b.id)
      })
      setArsomaPrintData(sortedResults)
      setIsSorting(false)
    })

    return () => unsubscribe()
  }, [arsomaSequence])

  //ARSOMA SEQUENCE
  const updateArsomaSequence = async (seq) => {
    const ref = doc(db, "sequence", "lvYzqVUCJhcyuwSlJESD")
    try {
      await updateDoc(ref, {
        arsomaSequence: seq,
      }).then(() => {
        message.success("updated!")
      })
    } catch (error) {
      console.log(error.message)
      message.error(error)
    }
  }

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setArsomaPrintData((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id)
        const overIndex = prev.findIndex((i) => i.key === over?.id)
        const newArsomaData = arrayMove(prev, activeIndex, overIndex)
        const newIdOrder = newArsomaData.map((item) => item.id)
        updateArsomaSequence(newIdOrder) //updates order online
        return newArsomaData
      })
    }
  }

  return (
    <>
      {/* ————— Active banner ————— */}
      <div className="active-box">
        {activeOrder ? (
          <Space split={<Divider type="vertical" />} align="center" wrap>
            <Badge color="red" text="Currently in use" />

            {/* Order link */}
            <NavLink to={`/arsoma/orders/${activeOrder.id}`} style={{ fontWeight: 500 }}>
              {ASAPOrder(activeOrder)}
              {OrderAndPrefix(activeOrder)}
            </NavLink>

            {/* Client */}
            <Text ellipsis={{ tooltip: activeOrder.client.client }}>
              {activeOrder.client.client}
            </Text>

            {/* Product */}
            <Text ellipsis={{ tooltip: activeOrder.selectedProduct.product.productName }}>
              {activeOrder.selectedProduct.product.productName}
            </Text>

            {/* Quantity */}
            <Text>{formatNumberWithSpaces(activeOrder.labelCount)}</Text>

            {/* Width */}
            <Text>{activeOrder.selectedProduct.labelWidth}</Text>

            {/* Height */}
            <Text>{activeOrder.selectedProduct.labelHeight}</Text>

            {/* Material */}
            <Text ellipsis={{ tooltip: activeOrder.selectedProduct.material }}>
              {activeOrder.selectedProduct.material}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">No order is currently active</Text>
        )}
      </div>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={arsomaPrintData.map((i) => i.key)}
          strategy={verticalListSortingStrategy}>
          <Table
            bordered
            size="small"
            className="custom-table"
            rowKey={(record) => record.id}
            dataSource={arsomaPrintData}
            loading={isSorting}
            columns={arsomaPrintingCols}
            rowClassName={(record) => {
              const classes = []
              if (record.priorityOrder) classes.push("red-background")
              if (record.currentlyInUseForArsoma) classes.push("active-row")
              return classes.join(" ")
            }}
            components={{
              body: {
                row: Row,
              },
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  )
}
